import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Radio,
  Table,
  Upload,
  message,
  Progress,
  Button,
  Avatar,
  Typography,
  Dropdown,
  Input,
  Modal,
  Spin,
  Tag,
  Tabs,
} from "antd";
import {
  UserOutlined,
  BankOutlined,
  FileTextOutlined,
  ToTopOutlined,
  SearchOutlined,
  ZoomInOutlined,
} from "@ant-design/icons";

const { TabPane } = Tabs;

export default function UserDetails() {
  const { userUuid } = useParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [provisionRequests, setProvisionRequests] = useState([]);
  const [userDetails, setUserDetails] = useState([]);
  const [subOrgLoading, setSubOrgLoading] = useState(false);
  const [provisionLoading, setProvisionLoading] = useState(false);

  const navigate = useNavigate();

  const fetchUserOrganisations = async () => {
    try {
      setSubOrgLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/${userUuid}/organisations`,
      );

      if (!res.ok) {
        throw new Error("Organisations introuvables");
      }

      const data = await res.json();
      setUserDetails(data);
    } catch (error) {
      console.error("Erreur récupération organisations :", error.message);
    } finally {
      setSubOrgLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/${userUuid}`,
      );

      if (!res.ok) {
        throw new Error("Erreur lors de la récupération des données");
      }

      const data = await res.json();
      setUsers(data);
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs:", error);
      message.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const fetchProvisionRequests = async () => {
    try {
      setProvisionLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/provision_requests`,
      );

      if (res.ok) {
        const requests = await res.json();
        const filteredRequests = requests.filter(
          (req) => req.userId === userUuid,
        );
        setProvisionRequests(filteredRequests);
      }
    } catch (error) {
      console.warn(
        "Erreur lors du chargement des demandes d'approvisionnement",
      );
    } finally {
      setProvisionLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchUserOrganisations();
    fetchProvisionRequests();
  }, []);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("fr-FR").format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return { bg: "#f6ffed", color: "#52c41a" };
      case "rejected":
        return { bg: "#fff2f0", color: "#ff4d4f" };
      case "pending":
        return { bg: "#fff7e6", color: "#fa8c16" };
      default:
        return { bg: "#f0f0f0", color: "#666" };
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Composant pour les informations générales
  const GeneralInfoTab = () => (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        padding: "20px",
      }}
    >
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "600",
          marginBottom: "20px",
          color: "#1890ff",
        }}
      >
        Informations générales
      </h3>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "20px",
        }}
      >
        <div>
          <strong>Nom de l'utilisateur :</strong>
          <div style={{ marginTop: "5px" }}>{users.fullname || "N/A"}</div>
        </div>
        <div>
          <strong>ID de l'utilisateur :</strong>
          <div
            style={{
              marginTop: "5px",
              fontFamily: "monospace",
              backgroundColor: "#f5f5f5",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            {users.userUuid || "N/A"}
          </div>
        </div>
        <div>
          <strong>Téléphone :</strong>
          <div style={{ marginTop: "5px" }}>+{users.phone || "N/A"}</div>
        </div>
        <div>
          <strong>Statut :</strong>
          <div style={{ marginTop: "5px" }}>
            <span
              style={{
                padding: "4px 12px",
                borderRadius: "4px",
                fontSize: "14px",
                backgroundColor:
                  users.status === "active" ? "#f6ffed" : "#fff2f0",
                color: users.status === "active" ? "#52c41a" : "#ff4d4f",
              }}
            >
              {users.status === "active" ? "Actif" : "Inactif"}
            </span>
          </div>
        </div>
        <div>
          <strong>Date de création :</strong>
          <div style={{ marginTop: "5px" }}>
            {users.createdAt
              ? new Date(users.createdAt).toLocaleString()
              : "N/A"}
          </div>
        </div>
        <div>
          <strong>Date de mise à jour :</strong>
          <div style={{ marginTop: "5px" }}>
            {users.updatedAt
              ? new Date(users.updatedAt).toLocaleString()
              : "N/A"}
          </div>
        </div>
      </div>
    </div>
  );

  // Composant pour les organisations
  const OrganisationsTab = () => (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        padding: "20px",
      }}
    >
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "600",
          marginBottom: "20px",
          color: "#722ed1",
        }}
      >
        Organisations ({userDetails.length})
      </h3>

      {subOrgLoading ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
        </div>
      ) : userDetails.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ backgroundColor: "#fafafa" }}>
              <tr>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Nom
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Type de compte
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Statut
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Numéro de l'organisation
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Rôle
                </th>
              </tr>
            </thead>
            <tbody>
              {userDetails.map((subOrg) => (
                <tr
                  key={subOrg.organisationId}
                  style={{ borderBottom: "1px solid #f0f0f0" }}
                >
                  <td style={{ padding: "12px" }}>{subOrg.name}</td>
                  <td style={{ padding: "12px" }}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        backgroundColor:
                          subOrg.account_type === "marchand"
                            ? "#e6f7ff"
                            : subOrg.account_type === "school"
                              ? "#fff7e6"
                              : subOrg.account_type === "pdv"
                                ? "#F0FFFF"
                                : subOrg.account_type === "enterprise"
                                  ? "#DDA0DD"
                                  : "#f5f5f5",
                        color:
                          subOrg.account_type === "marchand"
                            ? "#1890ff"
                            : subOrg.account_type === "school"
                              ? "#fa8c16"
                              : subOrg.account_type === "pdv"
                                ? "#20B2AA"
                                : subOrg.account_type === "enterprise"
                                  ? "#9932CC"
                                  : "#666",
                      }}
                    >
                      {subOrg.account_type}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        backgroundColor:
                          subOrg.status === "active" ? "#f6ffed" : "#fff2f0",
                        color:
                          subOrg.status === "active" ? "#52c41a" : "#ff4d4f",
                      }}
                    >
                      {subOrg.status === "active" ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        backgroundColor: "#f6ffed",
                        color: "#52c41a",
                      }}
                    >
                      {subOrg.phone}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        backgroundColor: "#f6ffed",
                        color: "#52c41a",
                      }}
                    >
                      {subOrg.meta.pivot_role}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#666",
            backgroundColor: "#fafafa",
            borderRadius: "8px",
          }}
        >
          Aucune organisation trouvée
        </div>
      )}
    </div>
  );

  // Composant pour les demandes d'approvisionnement
  const ProvisionRequestsTab = () => (
    <div
      style={{
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        padding: "20px",
      }}
    >
      <h3
        style={{
          fontSize: "18px",
          fontWeight: "600",
          marginBottom: "20px",
          color: "#f5222d",
        }}
      >
        Demandes d'approvisionnement ({provisionRequests.length})
      </h3>

      {provisionLoading ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
        </div>
      ) : provisionRequests.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ backgroundColor: "#fafafa" }}>
              <tr>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Référence
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Montant
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Statut
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Type
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Fournisseur de paiement
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Date de création
                </th>
                <th
                  style={{
                    padding: "12px",
                    textAlign: "center",
                    fontWeight: "600",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  Document
                </th>
              </tr>
            </thead>
            <tbody>
              {provisionRequests.map((request) => {
                const statusColors = getStatusColor(request.status);
                return (
                  <tr
                    key={request.id}
                    style={{ borderBottom: "1px solid #f0f0f0" }}
                  >
                    <td
                      style={{
                        padding: "12px",
                        fontFamily: "monospace",
                        fontSize: "12px",
                      }}
                    >
                      {request.paymentProviderReference}
                    </td>
                    <td style={{ padding: "12px", fontWeight: "600" }}>
                      {formatAmount(request.amount)} CFA
                    </td>
                    <td style={{ padding: "12px" }}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          backgroundColor: statusColors.bg,
                          color: statusColors.color,
                        }}
                      >
                        {request.status === "approved"
                          ? "Approuvé"
                          : request.status === "rejected"
                            ? "Rejeté"
                            : request.status === "pending"
                              ? "En attente"
                              : request.status}
                      </span>
                    </td>
                    <td style={{ padding: "12px" }}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          backgroundColor: "#f0f2f5",
                          color: "#666",
                        }}
                      >
                        {request.provisionType}
                      </span>
                    </td>
                    <td style={{ padding: "12px" }}>
                      {request.paymentProvider ? (
                        <div>
                          <div style={{ fontWeight: "500" }}>
                            {request.paymentProvider.label}
                          </div>
                          <div style={{ fontSize: "12px", color: "#666" }}>
                            {request.paymentProvider.number}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: "#999" }}>N/A</span>
                      )}
                    </td>
                    <td style={{ padding: "12px", fontSize: "12px" }}>
                      {new Date(request.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      {request.documentUrl ? (
                        <a
                          href={request.documentUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "#1890ff",
                            textDecoration: "none",
                            fontSize: "12px",
                          }}
                        >
                          📄 Voir
                        </a>
                      ) : (
                        <span style={{ color: "#999", fontSize: "12px" }}>
                          N/A
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#666",
            backgroundColor: "#fafafa",
            borderRadius: "8px",
          }}
        >
          Aucune demande d'approvisionnement trouvée
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      {/* Message d'alerte */}
      {message.text && (
        <div
          style={{
            padding: "10px",
            marginBottom: "20px",
            borderRadius: "4px",
            backgroundColor: message.type === "error" ? "#ffebee" : "#e8f5e8",
            color: message.type === "error" ? "#c62828" : "#2e7d32",
            border: `1px solid ${
              message.type === "error" ? "#ef5350" : "#66bb6a"
            }`,
          }}
        >
          {message.text}
        </div>
      )}

      {/* Header avec bouton retour */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "28px", fontWeight: "600" }}>
          Détails de l'utilisateur
        </h1>
        <button
          onClick={() => navigate("/users")}
          style={{
            padding: "8px 16px",
            backgroundColor: "#f5f5f5",
            color: "#333",
            border: "1px solid #d9d9d9",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          ← Retour à la liste
        </button>
      </div>

      {/* Tabs pour organiser le contenu */}
      <Tabs defaultActiveKey="1" type="card" size="large">
        <TabPane
          tab={
            <span>
              <UserOutlined />
              Informations générales
            </span>
          }
          key="1"
        >
          <GeneralInfoTab />
        </TabPane>

        <TabPane
          tab={
            <span>
              <BankOutlined />
              Organisations ({userDetails.length})
            </span>
          }
          key="2"
        >
          <OrganisationsTab />
        </TabPane>

        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              Demandes d'approvisionnement ({provisionRequests.length})
            </span>
          }
          key="3"
        >
          <ProvisionRequestsTab />
        </TabPane>
      </Tabs>
    </div>
  );
}
